import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useSessionStore } from '../stores/sessionStore';
import { createFHIRClient } from './client';
// EH-03 / EH-12 — shared encounter-linking + US Core profile-stamping helper.
// withEncounter sets the correct encounter reference per resource type (per
// ENCOUNTER_FIELD_MAP contract). withUsCoreProfile stamps meta.profile.
// Pattern: withUsCoreProfile(withEncounter(resource, encounterId))
// TODO EH-16: pass { region: 'in' } for India tenants once a tenant-region
// signal is available in the session/JWT claims.
import { withEncounter, withUsCoreProfile } from '@collabkare/encounter-fhir';
// EH-06 — one-shot Firestore read to assemble the final transcript at visit-end.
// Uses getDocs (not onSnapshot) so we get a stable snapshot of all committed
// chunks rather than an open subscription. The chunks subcollection is written
// by the televisit-stt-ingest service and STAYS in Firestore (Bucket C realtime
// store); only the assembled durable text moves to FHIR.
import {
  collection,
  getDocs,
  orderBy,
  query as firestoreQuery,
} from 'firebase/firestore';
import { firestore } from '../firebase/config';

/**
 * ============================================================================
 * FHIR Query Hooks
 * ============================================================================
 *
 * TanStack Query hooks for common FHIR resource queries.
 * Works transparently in both EHR launch and standalone modes.
 *
 * In EHR mode: uses fhirclient's authenticated Client (handles token refresh)
 * In Standalone mode: uses manual fetch with Keycloak token
 * In Dev mode: queries are disabled (no real FHIR server)
 *
 * Query keys follow: [resourceType, id, ...filters]
 * ============================================================================
 */

/**
 * Hook that creates the appropriate FHIR client based on current auth mode.
 * Memoized so it doesn't recreate on every render.
 */
export function useFHIR() {
  const { authMode, token, fhirClient } = useAuth();
  const { tenantId } = useSessionStore();

  const client = useMemo(() => {
    if (authMode === 'dev') return null;
    // TV-P1-06 hardening: standalone mode can't build a valid FHIR URL
    // without a tenantId. Hold off creating the client (and let `isReady`
    // gate queries) rather than letting `r4.collabkare.io/undefined/data/...`
    // 404 silently. EHR mode is fine — fhirclient uses the iss-derived
    // serverUrl, which already contains the tenant + /data path.
    if (authMode === 'standalone' && !tenantId) {
      if (token) {
        console.warn(
          '[FHIR] standalone mode is waiting on tenantId — ' +
          'AuthProvider should call setTenantId(fhirTenantId) before FHIR queries run.',
        );
      }
      return null;
    }
    return createFHIRClient({ authMode, fhirClient, token, tenantId });
  }, [authMode, fhirClient, token, tenantId]);

  const isReady = authMode === 'dev'
    ? false // dev mode: no real FHIR calls
    : !!client && !!token;

  return { client, isReady, authMode };
}

/**
 * Fetches a single Patient resource.
 */
export function usePatient(patientId) {
  const { client, isReady } = useFHIR();

  return useQuery({
    queryKey: ['Patient', patientId],
    queryFn: () => client.get(`Patient/${patientId}`),
    enabled: isReady && !!patientId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetches a comprehensive patient summary:
 * active conditions, current meds, allergies, recent labs, recent vitals.
 */
export function usePatientSummary(patientId) {
  const { client, isReady } = useFHIR();

  return useQuery({
    queryKey: ['PatientSummary', patientId],
    queryFn: async () => {
      // Patient read is required — if THIS fails the whole panel is
      // meaningless and we propagate. The six secondary searches are
      // independent — use allSettled so one bad sort param or missing
      // resource type doesn't blank the rest of the chart.
      const patient = await client.get(`Patient/${patientId}`);

      const sections = await Promise.allSettled([
        client.search('Condition', { patient: patientId, 'clinical-status': 'active', _sort: '-recorded-date', _count: 10 }),
        client.search('MedicationStatement', { patient: patientId, status: 'active', _sort: '-effective', _count: 10 }),
        client.search('AllergyIntolerance', { patient: patientId, _sort: '-date', _count: 10 }),
        client.search('Observation', { patient: patientId, category: 'laboratory', _sort: '-date', _count: 10 }),
        client.search('Observation', { patient: patientId, category: 'vital-signs', _sort: '-date', _count: 5 }),
      ]);

      const labels = ['Condition', 'MedicationStatement', 'AllergyIntolerance', 'Observation(lab)', 'Observation(vitals)'];
      const pick = (i) => {
        const r = sections[i];
        if (r.status === 'fulfilled') return r.value?.entry || [];
        // Log structured so failures surface in the console but the UI
        // keeps rendering what it could fetch. The empty-section view in
        // ChartTab covers the missing data.
        console.warn(`[usePatientSummary] ${labels[i]} fetch failed:`, r.reason?.message || r.reason);
        return [];
      };

      return {
        patient,
        conditions: pick(0),
        medications: pick(1),
        allergies: pick(2),
        labs: pick(3),
        vitals: pick(4),
      };
    },
    enabled: isReady && !!patientId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetches a single Encounter resource.
 */
export function useEncounter(encounterId) {
  const { client, isReady } = useFHIR();

  return useQuery({
    queryKey: ['Encounter', encounterId],
    queryFn: () => client.get(`Encounter/${encounterId}`),
    enabled: isReady && !!encounterId,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * TV-P1-05 — Care gaps for a patient.
 *
 * Source priority:
 *   1. HEDIS-grade `MeasureReport` rows from the populationhealth
 *      worker. Care gap = patient is in the denominator (eligible)
 *      but not in the numerator (action satisfied). Each gap
 *      includes the measure display + the report's reporting period
 *      so the UI can surface "last assessed" without an extra fetch.
 *   2. Heuristic fallback derived from active Conditions
 *      (ICD-10 prefix → recommended measure). Only used when the
 *      MeasureReport search returns zero rows — keeps clinically
 *      useful nudges flowing for tenants that haven't run a
 *      MeasureReport job yet.
 *
 * Returns a flat array of gap rows shaped as
 *   { id, gap, status, lastDate, priority, source }
 * `source` is `'measureReport'` or `'heuristic'` for downstream UIs
 * that want to badge a "MeasureReport not yet computed" hint.
 */
export function useCareGaps(patientId) {
  const { client, isReady } = useFHIR();

  return useQuery({
    queryKey: ['CareGaps', patientId],
    queryFn: async () => {
      const fromMeasureReports = await fetchGapsFromMeasureReports(client, patientId);
      if (fromMeasureReports.length > 0) {
        return { gaps: fromMeasureReports, source: 'measureReport' };
      }

      // Fallback: condition-derived nudges.
      try {
        const conditions = await client.search('Condition', {
          patient: patientId,
          'clinical-status': 'active',
        });
        const gaps = deriveGapsFromConditions(conditions?.entry || []);
        return { gaps, source: 'heuristic' };
      } catch (err) {
        console.warn('[FHIR] Could not derive heuristic care gaps:', err);
        return { gaps: [], source: 'none' };
      }
    },
    enabled: isReady && !!patientId,
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Fetches a Questionnaire definition.
 */
export function useQuestionnaire(questionnaireId) {
  const { client, isReady } = useFHIR();

  return useQuery({
    queryKey: ['Questionnaire', questionnaireId],
    queryFn: () => client.get(`Questionnaire/${questionnaireId}`),
    enabled: isReady && !!questionnaireId,
    staleTime: 30 * 60 * 1000,
  });
}

/**
 * Fetches QuestionnaireResponses for a patient + questionnaire.
 */
export function useQuestionnaireResponses(patientId, questionnaireId) {
  const { client, isReady } = useFHIR();

  return useQuery({
    queryKey: ['QuestionnaireResponse', patientId, questionnaireId],
    queryFn: () =>
      client.search('QuestionnaireResponse', {
        subject: `Patient/${patientId}`,
        questionnaire: `Questionnaire/${questionnaireId}`,
        _sort: '-authored',
        _count: 10,
      }),
    enabled: isReady && !!patientId && !!questionnaireId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * TV-P1-09 — list every QuestionnaireResponse for a patient (no
 * specific Questionnaire required). Powers the Assess tab's
 * "completed / in-progress assessments" view.
 */
export function usePatientQuestionnaireResponses(patientId) {
  const { client, isReady } = useFHIR();
  return useQuery({
    queryKey: ['QuestionnaireResponse', 'byPatient', patientId],
    queryFn: () =>
      client.search('QuestionnaireResponse', {
        subject: `Patient/${patientId}`,
        _sort: '-authored',
        _count: 20,
      }),
    enabled: isReady && !!patientId,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Fetches a CarePlan for an encounter (or patient fallback).
 */
export function useCarePlan(encounterId, patientId) {
  const { client, isReady } = useFHIR();

  return useQuery({
    queryKey: ['CarePlan', encounterId, patientId],
    queryFn: async () => {
      try {
        return await client.search('CarePlan', {
          encounter: encounterId,
          status: 'active',
          _sort: '-date',
          _count: 1,
        });
      } catch {
        return client.search('CarePlan', {
          subject: `Patient/${patientId}`,
          status: 'active',
          _sort: '-date',
          _count: 1,
        });
      }
    },
    enabled: isReady && !!(encounterId || patientId),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * TV-P1-02 — Today's appointments for a practitioner.
 *
 * Searches `Appointment?practitioner={id}&date=ge{today}&date=lt{tomorrow}
 * &status=booked,arrived,fulfilled&_include=Appointment:patient`. The
 * include pulls Patient resources in the same Bundle so the queue UI
 * doesn't have to N+1 chase references.
 *
 * `practitionerId` may arrive as a bare id (`123`) or as a reference
 * (`Practitioner/123`); both are accepted.
 */
export function useTodaysAppointments(practitionerId) {
  const { client, isReady } = useFHIR();

  // Normalize practitioner ref → bare id for the search param.
  const practitionerRef = practitionerId
    ? practitionerId.includes('/')
      ? practitionerId.split('/').slice(-1)[0]
      : practitionerId
    : null;

  return useQuery({
    queryKey: ['Appointment', 'today', practitionerRef],
    queryFn: async () => {
      // Local-day window. IMPORTANT: send full ISO timestamps, not
      // date-only strings. `.toISOString().slice(0, 10)` converts the
      // LOCAL midnight to a UTC calendar date, so an evening appointment
      // (e.g. 9:52 PM EDT = 01:52Z next day) fell outside the date-only
      // window and silently vanished from the queue.
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(end.getDate() + 1);

      const result = await client.search('Appointment', {
        practitioner: `Practitioner/${practitionerRef}`,
        date: [`ge${start.toISOString()}`, `lt${end.toISOString()}`],
        status: 'booked,arrived,fulfilled,checked-in',
        _include: 'Appointment:patient',
        _sort: 'date',
        _count: 50,
      });

      // Split the bundle into Appointments + a Patient index for quick lookup.
      const allEntries = result?.entry || [];
      const appointments = allEntries.filter((e) => e.resource?.resourceType === 'Appointment');
      const patients = {};
      for (const e of allEntries) {
        if (e.resource?.resourceType === 'Patient') {
          patients[e.resource.id] = e.resource;
        }
      }
      return { appointments, patients };
    },
    enabled: isReady && !!practitionerRef,
    staleTime: 60 * 1000, // 1 min — queue refreshes often during a shift
  });
}

// ── EH-06 — Transcript DocumentReference helpers ─────────────────────────────

/**
 * Reads the final transcript chunks for an encounter from Firestore (one-shot
 * getDocs, NOT a live subscription) and concatenates them into a plain-text
 * string ordered by receivedAt ASC. Only `isFinal === true` chunks are
 * included so interim STT results don't bloat the permanent record. Falls back
 * to all chunks if none are marked final (e.g. ingest didn't set the flag).
 *
 * Returns '' if the collection is empty or Firestore is unavailable.
 *
 * @param {string} encounterId - FHIR Encounter id (= Firestore doc id)
 * @returns {Promise<string>}
 */
async function assembleTranscriptText(encounterId) {
  if (!firestore || !encounterId) return '';
  try {
    const chunksRef = collection(
      firestore,
      'televisit-transcripts',
      encounterId,
      'chunks',
    );
    const q = firestoreQuery(chunksRef, orderBy('receivedAt', 'asc'));
    const snap = await getDocs(q);
    if (snap.empty) return '';

    const docs = snap.docs.map((d) => d.data());
    // Prefer isFinal chunks; fall back to all chunks so a visit where
    // the ingest service never set isFinal still produces a transcript.
    const finalDocs = docs.filter((d) => d.isFinal === true);
    const source = finalDocs.length > 0 ? finalDocs : docs;

    return source
      .map((d) => {
        // Prefix each line with the speaker identity when available so
        // the persisted record is human-readable without the Firestore doc.
        const speaker = d.participantIdentity ? `${d.participantIdentity}: ` : '';
        return `${speaker}${d.text || ''}`;
      })
      .filter(Boolean)
      .join('\n');
  } catch (err) {
    console.warn('[EH-06] assembleTranscriptText Firestore read failed:', err?.message || err);
    return '';
  }
}

/**
 * EH-06 — write ONE FHIR DocumentReference containing the assembled visit
 * transcript, linked to the Encounter and Patient.
 *
 * Design:
 *  - Idempotent: searches for an existing DocumentReference with the same
 *    encounter + type code BEFORE writing. If one exists, logs and returns
 *    early (handles the visit-end-runs-twice case).
 *  - Non-blocking: callers must wrap this in their own try/catch. A failure
 *    here must NOT block the Encounter → finished transition or billing.
 *  - Profile: stamped via withUsCoreProfile + withEncounter per the shared
 *    helper contract.
 *  - Type: LOINC 11506-3 "Progress note" (the conventional code for encounter
 *    transcripts/clinical encounter notes in FHIR DocumentReference. An
 *    institution-specific code like 73568-0 "Telemedicine consultation note"
 *    can be substituted when the LOINC ValueSet is reviewed in EH-18.)
 *  - TODO EH-16: pass { region: 'in' } to withUsCoreProfile for India tenants.
 *
 * @param {Object} client  - collabkare FHIR client (from useFHIR)
 * @param {string} encounterId - FHIR Encounter resource id
 * @param {string} patientId   - FHIR Patient resource id
 * @param {string} visitEndISO - ISO timestamp of visit end (period.end)
 * @returns {Promise<Object|null>} The created DocumentReference, or null on skip/error.
 */
async function writeTranscriptDocumentReference(client, encounterId, patientId, visitEndISO) {
  if (!client || !encounterId) {
    console.warn('[EH-06] writeTranscriptDocumentReference: missing client or encounterId — skipping.');
    return null;
  }

  // ── Idempotency check ────────────────────────────────────────────────────
  // Search for an existing transcript DocumentReference for this encounter.
  // LOINC 11506-3 + encounter is specific enough to be a stable dedup key
  // for a single visit. Use allSettled pattern — if the search itself fails
  // (e.g. HAPI returns 400 on an unsupported search param), log and proceed
  // to the write anyway rather than silently dropping the record.
  try {
    const existing = await client.search('DocumentReference', {
      encounter: encounterId,
      type: 'http://loinc.org|11506-3',
      _count: 1,
    });
    const hits = existing?.entry || [];
    if (hits.length > 0) {
      console.info(
        `[EH-06] Transcript DocumentReference already exists for Encounter/${encounterId} — skipping duplicate write.`,
      );
      return null;
    }
  } catch (searchErr) {
    // Non-fatal: if the search blows up (e.g. HAPI 400 on unsupported param
    // combination), log and proceed — a duplicate is better than a missing record.
    console.warn(
      '[EH-06] Idempotency search failed — proceeding with write:',
      searchErr?.message || searchErr,
    );
  }

  // ── Assemble transcript text from Firestore ──────────────────────────────
  const transcriptText = await assembleTranscriptText(encounterId);
  if (!transcriptText) {
    console.info(
      `[EH-06] No transcript chunks found for Encounter/${encounterId} — DocumentReference will contain empty attachment.`,
    );
    // Still write the shell DocumentReference so the FHIR record exists;
    // a provider can attach content later. Empty attachment is valid FHIR.
  }

  // ── Build the DocumentReference ──────────────────────────────────────────
  // base64-encode the transcript text for the FHIR attachment. Using
  // btoa(unescape(encodeURIComponent(...))) to handle non-ASCII speech output
  // without throwing "character out of range" on UTF-8 content.
  const base64Content = transcriptText
    ? btoa(unescape(encodeURIComponent(transcriptText)))
    : '';

  const docRef = withUsCoreProfile(
    withEncounter(
      {
        resourceType: 'DocumentReference',
        status: 'current',
        docStatus: 'final',
        // LOINC 11506-3 = Progress note (encounter documentation)
        // Represents the transcript/notes produced during a clinical encounter.
        type: {
          coding: [
            {
              system: 'http://loinc.org',
              code: '11506-3',
              display: 'Progress note',
            },
          ],
          text: 'Visit Transcript',
        },
        subject: { reference: `Patient/${patientId}` },
        date: visitEndISO || new Date().toISOString(),
        // category: Clinical note (us-core-documentreference required element)
        category: [
          {
            coding: [
              {
                system: 'http://hl7.org/fhir/us/core/CodeSystem/us-core-documentreference-category',
                code: 'clinical-note',
                display: 'Clinical Note',
              },
            ],
          },
        ],
        content: [
          {
            attachment: {
              contentType: 'text/plain',
              data: base64Content,
              title: `Visit Transcript — Encounter ${encounterId}`,
              creation: visitEndISO || new Date().toISOString(),
            },
          },
        ],
        // context.encounter is set by withEncounter() above via the
        // ENCOUNTER_FIELD_MAP in @collabkare/encounter-fhir.
        // Explicit context.period mirrors the encounter for queryability.
        context: {
          period: {
            end: visitEndISO || new Date().toISOString(),
          },
        },
      },
      encounterId,
      // TODO EH-16: pass { region: 'in' } as third arg to withEncounter
      // once a tenant-region signal is available in the session/JWT claims.
    ),
  );

  const saved = await client.post('DocumentReference', docRef);
  console.info(
    `[EH-06] Transcript DocumentReference created: DocumentReference/${saved?.id} for Encounter/${encounterId}`,
  );
  return saved;
}

/**
 * TV-P1-03 — Create an Encounter when a provider clicks Start Visit.
 *
 * POSTs the FHIR-R4 minimal Encounter shape:
 *   {
 *     resourceType: 'Encounter',
 *     status: 'in-progress',
 *     class: { system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
 *              code: 'VR', display: 'virtual' },
 *     subject: { reference: 'Patient/{patientId}' },
 *     participant: [{ individual: { reference: 'Practitioner/{practitionerId}' } }],
 *     period: { start: <ISO now> },
 *     appointment: [{ reference: 'Appointment/{appointmentId}' }],
 *     reasonCode: [{ text: chiefComplaint }]
 *   }
 *
 * On success, invalidates today's appointment queue + the matched
 * Encounter cache so the queue badge transitions without a manual
 * refresh. Returns the persisted Encounter (with server id) via the
 * mutation's onSuccess data so callers can route to /visit/{id}.
 */
export function useCreateEncounter() {
  const { client, isReady } = useFHIR();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (args) => {
      if (!isReady || !client) {
        throw new Error('FHIR client is not ready — auth + tenant must be resolved before creating an Encounter.');
      }
      const { patientId, practitionerId, appointmentId, chiefComplaint } = args || {};
      if (!patientId) throw new Error('createEncounter: patientId is required');

      // Strip a "Practitioner/" prefix if present — both forms tolerated.
      const practitionerBare = practitionerId
        ? (practitionerId.includes('/')
          ? practitionerId.split('/').slice(-1)[0]
          : practitionerId)
        : null;

      // EH-12 — Encounter is the contextual anchor; stamp its US Core profile
      // before POST. No self-encounter ref (it's the anchor itself).
      // class/appointment/participant/period/reasonCode are preserved — they
      // are carried through the spread into withUsCoreProfile's shallow clone.
      const body = withUsCoreProfile({
        resourceType: 'Encounter',
        status: 'in-progress',
        class: {
          system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
          code: 'VR',
          display: 'virtual',
        },
        subject: { reference: `Patient/${patientId}` },
        ...(practitionerBare && {
          participant: [
            {
              individual: { reference: `Practitioner/${practitionerBare}` },
            },
          ],
        }),
        period: { start: new Date().toISOString() },
        ...(appointmentId && {
          appointment: [{ reference: `Appointment/${appointmentId}` }],
        }),
        ...(chiefComplaint && {
          reasonCode: [{ text: chiefComplaint }],
        }),
      });

      const saved = await client.post('Encounter', body);
      if (!saved?.id) {
        throw new Error('FHIR server did not return an Encounter id on create.');
      }
      return saved;
    },
    onSuccess: (saved) => {
      // Bust caches that depend on Appointment/Encounter freshness so
      // the next refetch reflects the new row immediately.
      queryClient.invalidateQueries({ queryKey: ['Appointment', 'today'] });
      if (saved?.id) {
        queryClient.setQueryData(['Encounter', saved.id], saved);
        // EH-03 — push the new encounter id into the session store so
        // subsequent FHIR writes (QR, Observations) in the same visit can
        // pick it up via activeEncounterId without waiting for ProviderQueue
        // or LiveVisit to set it.
        useSessionStore.getState().setCurrentEncounter(saved.id);
      }
    },
  });
}

/**
 * TV-P1-04 — Finish an in-progress Encounter.
 *
 * Fetches the current Encounter, merges in `status: 'finished'`,
 * `period.end: <ISO now>`, and an optional `reasonCode` from the
 * visit's chief complaint, then PUTs it back. Read-merge-write is
 * intentional: HAPI replaces the whole resource on PUT, so we keep
 * subject / participant / appointment / class intact rather than
 * sending a sparse body that would null those refs out.
 *
 * On success, invalidates the cached Encounter + today's queue so
 * the row's bucket flips to 'completed' on the next refetch.
 */
export function useUpdateEncounter() {
  const { client, isReady } = useFHIR();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (args) => {
      if (!isReady || !client) {
        throw new Error('FHIR client is not ready — auth + tenant must be resolved before updating an Encounter.');
      }
      const { encounterId, reasonText } = args || {};
      if (!encounterId) throw new Error('updateEncounter: encounterId is required');

      const current = await client.get(`Encounter/${encounterId}`);
      if (!current?.id) {
        throw new Error(`Encounter/${encounterId} not found.`);
      }

      const body = {
        ...current,
        status: 'finished',
        period: {
          ...(current.period || {}),
          end: new Date().toISOString(),
        },
        // Preserve any existing reasonCode; if the caller passes
        // reasonText and there's none on record, attach it.
        ...(!current.reasonCode?.length && reasonText
          ? { reasonCode: [{ text: reasonText }] }
          : {}),
      };

      const saved = await client.put(`Encounter/${encounterId}`, body);
      if (!saved?.id) {
        throw new Error('FHIR server did not return an Encounter on update.');
      }

      // Also mark the linked Appointment fulfilled. Before this, ending
      // a visit left the Appointment `booked` forever — the provider
      // queue kept listing the patient as Scheduled and the Completed
      // tile never moved. Non-critical: a failure here must not undo
      // the finished Encounter, so log and continue.
      const apptRef = current.appointment?.[0]?.reference;
      if (apptRef) {
        try {
          const appt = await client.get(apptRef);
          if (appt?.id && appt.status !== 'fulfilled') {
            await client.put(apptRef, { ...appt, status: 'fulfilled' });
          }
        } catch (apptErr) {
          console.warn(
            `[useUpdateEncounter] could not fulfil ${apptRef}:`,
            apptErr?.message || apptErr,
          );
        }
      }

      // EH-06 — persist the visit transcript as a FHIR DocumentReference
      // linked to this Encounter. Non-blocking: a failure here MUST NOT
      // prevent the visit from ending (Encounter→finished + billing are the
      // critical path). Idempotency is handled inside
      // writeTranscriptDocumentReference (searches before writing).
      //
      // patientId is resolved from the saved Encounter subject reference so
      // this hook doesn't need it injected from the call site — all the
      // data is already on the Encounter we just PUT.
      const patientRef = saved.subject?.reference || '';
      const patientId = patientRef.startsWith('Patient/')
        ? patientRef.slice('Patient/'.length)
        : patientRef;
      const visitEndISO = saved.period?.end || new Date().toISOString();

      try {
        await writeTranscriptDocumentReference(client, encounterId, patientId, visitEndISO);
      } catch (transcriptErr) {
        // Intentionally catch-and-log: the visit is finished, billing fires
        // next in LiveVisit.handleEndVisit. A transcript DocumentReference
        // failure is serious enough to log as error (not warn) so it surfaces
        // in Cloud Logging, but it must not surface a blocking UI error.
        console.error(
          `[EH-06] Failed to write transcript DocumentReference for Encounter/${encounterId}:`,
          transcriptErr?.message || transcriptErr,
        );
      }

      return saved;
    },
    onSuccess: (saved) => {
      if (saved?.id) {
        queryClient.setQueryData(['Encounter', saved.id], saved);
      }
      queryClient.invalidateQueries({ queryKey: ['Appointment', 'today'] });
    },
  });
}

/**
 * TV-P5-05 + TV-P5-06 — POST a QuestionnaireResponse and (optionally)
 * the Observations extracted from it.
 *
 * Two flavors:
 *   - args.observations omitted → single QR POST.
 *   - args.observations present → wraps QR + Observations in a
 *     FHIR transaction Bundle so the whole submission is atomic.
 *     Observations reference the QR via `urn:uuid:...` fullUrl
 *     placeholders that HAPI resolves on commit, so the QR id only
 *     materializes server-side.
 *
 * On success, invalidates the patient's QR + Observation caches so
 * the next chart refresh shows the new rows.
 */
export function useSaveQuestionnaireResponse() {
  const { client, isReady } = useFHIR();
  const queryClient = useQueryClient();
  // EH-03 / EH-12 — active encounter id for encounter-linking on QR + Observations.
  // Prefer the store's currentEncounter (set by useCreateEncounter.onSuccess);
  // fall back to smartContext.encounterId (EHR-launch context).
  const { currentEncounter, smartContext } = useSessionStore();
  const activeEncounterId = currentEncounter || smartContext?.encounterId || null;

  return useMutation({
    mutationFn: async (args) => {
      if (!isReady || !client) {
        throw new Error(
          'FHIR client is not ready — auth + tenant must be resolved before saving a QuestionnaireResponse.',
        );
      }
      const { qr, observations } = args || {};
      if (!qr || qr.resourceType !== 'QuestionnaireResponse') {
        throw new Error('saveQuestionnaireResponse: qr is required and must be a QuestionnaireResponse');
      }

      // EH-03 / EH-12 — stamp encounter ref + US Core profile on QR.
      // withEncounter sets qr.encounter = { reference: 'Encounter/<id>' } (no-op if no
      // active encounter or if QR type not in the map — both safe).
      // withUsCoreProfile adds meta.profile for QuestionnaireResponse.
      // TODO EH-16: pass { region: 'in' } once tenant-region signal is available.
      const stampedQr = withUsCoreProfile(withEncounter(qr, activeEncounterId));

      // Simple path: just the QR.
      if (!observations || observations.length === 0) {
        const saved = await client.post('QuestionnaireResponse', stampedQr);
        if (!saved?.id) {
          throw new Error('FHIR server did not return a QuestionnaireResponse id.');
        }
        return { qr: saved, observations: [] };
      }

      // Transaction path: QR + N Observations, atomic.
      const qrFullUrl = `urn:uuid:${
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `qr-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      }`;

      // EH-03 / EH-12 — stamp encounter ref + US Core profile on each Observation.
      // extractObservations already conditionally sets obs.encounter when encounterId
      // is passed to it; withEncounter is idempotent if already set, so running it
      // here is safe and ensures profile stamping regardless of call path.
      // TODO EH-16: pass { region: 'in' } once tenant-region signal is available.
      const stampedObservations = observations.map((obs) =>
        withUsCoreProfile(withEncounter(obs, activeEncounterId)),
      );

      const bundle = {
        resourceType: 'Bundle',
        type: 'transaction',
        entry: [
          {
            fullUrl: qrFullUrl,
            resource: stampedQr,
            request: { method: 'POST', url: 'QuestionnaireResponse' },
          },
          ...stampedObservations.map((obs) => ({
            resource: {
              ...obs,
              // Rewrite the per-Observation derivedFrom to point at
              // the placeholder fullUrl rather than the (yet-unknown)
              // server-assigned QR id. HAPI resolves on commit.
              derivedFrom: [{ reference: qrFullUrl }],
            },
            request: { method: 'POST', url: 'Observation' },
          })),
        ],
      };

      const result = await client.batch(bundle);
      // The response is a transaction-response Bundle.
      const entries = result?.entry || [];
      const savedQr = entries[0]?.response?.location
        ? { ...qr, id: entries[0].response.location.match(/QuestionnaireResponse\/([^/]+)/)?.[1] }
        : qr;
      const savedObs = entries.slice(1).map((e, i) => ({
        ...observations[i],
        id: e?.response?.location?.match(/Observation\/([^/]+)/)?.[1],
      }));
      return { qr: savedQr, observations: savedObs };
    },
    onSuccess: ({ qr }) => {
      const patientRef = qr?.subject?.reference || '';
      const patientId = patientRef.replace(/^Patient\//, '');
      if (patientId) {
        queryClient.invalidateQueries({
          queryKey: ['QuestionnaireResponse', 'byPatient', patientId],
        });
        queryClient.invalidateQueries({
          queryKey: ['Observation', patientId],
        });
      }
    },
  });
}

/**
 * Fetches Observations for a patient (optionally filtered by category).
 */
export function useObservations(patientId, category = null) {
  const { client, isReady } = useFHIR();

  return useQuery({
    queryKey: ['Observation', patientId, category],
    queryFn: () => {
      const params = { patient: patientId, _sort: '-date', _count: 20 };
      if (category) params.category = category;
      return client.search('Observation', params);
    },
    enabled: isReady && !!patientId,
    staleTime: 5 * 60 * 1000,
  });
}

// ── Care-gap helpers (TV-P1-05) ─────────────────────────────────────────────

/**
 * Pulls open care gaps from this patient's recent MeasureReports.
 *
 * Resource shape varies by quality engine, but the common pattern is:
 *   MeasureReport.group[].population[]
 *     where coding.code in {initial-population, denominator, numerator,
 *                           denominator-exclusion, denominator-exception}
 * If the patient is in the denominator but not the numerator, the
 * measure has an open gap.
 *
 * Returns a flat array of gap rows ready for the UI; empty array on
 * any failure (including 404 / 403 / network) so callers can
 * cleanly fall back.
 */
async function fetchGapsFromMeasureReports(client, patientId) {
  try {
    const result = await client.search('MeasureReport', {
      subject: `Patient/${patientId}`,
      _sort: '-date',
      _count: 20,
    });
    const rows = result?.entry || [];
    if (rows.length === 0) return [];

    const gaps = [];
    let nextId = 1;
    for (const entry of rows) {
      const mr = entry.resource;
      if (!mr) continue;

      const measureLabel = measureLabelFromReport(mr);
      const lastDate = mr.period?.end || mr.period?.start || mr.date || null;

      const groups = mr.group || [];
      for (const g of groups) {
        const pops = g.population || [];
        const denominator = populationCount(pops, 'denominator');
        const numerator = populationCount(pops, 'numerator');
        // No denominator → patient isn't eligible for this measure; skip.
        if (denominator === 0) continue;
        // Numerator covers denominator → measure satisfied; skip.
        if (numerator >= denominator) continue;

        const groupLabel =
          g.code?.text
          || g.code?.coding?.[0]?.display
          || measureLabel
          || 'Care measure';

        gaps.push({
          id: `mr-${mr.id || nextId}-${groupLabel}`,
          gap: groupLabel,
          status: agedStatus(lastDate),
          lastDate: lastDate ? lastDate.slice(0, 10) : null,
          priority: priorityForMeasure(groupLabel),
          source: 'measureReport',
        });
        nextId++;
      }
    }
    return gaps;
  } catch (err) {
    // Don't log a noisy error on 404 — many tenants haven't run a
    // MeasureReport pipeline yet, and the fallback handles it.
    if (err?.status && err.status !== 404) {
      console.warn('[FHIR] MeasureReport care-gap fetch failed:', err);
    }
    return [];
  }
}

function populationCount(populations, codeWanted) {
  for (const p of populations || []) {
    const code = (p.code?.coding || []).find((c) => c.code === codeWanted);
    if (code) return p.count ?? 0;
  }
  return 0;
}

function measureLabelFromReport(mr) {
  if (!mr) return null;
  // Resolve the measure canonical URL → friendly label. HEDIS measures
  // often have a `meta.tag` or `extension` carrying the display; fall
  // back to the canonical URL's terminal segment.
  if (mr.measure) {
    const trailing = String(mr.measure).split('/').slice(-1)[0];
    return trailing.replace(/[-_]/g, ' ');
  }
  return null;
}

function agedStatus(isoDate) {
  if (!isoDate) return 'due';
  const t = Date.parse(isoDate);
  if (!Number.isFinite(t)) return 'due';
  const days = Math.floor((Date.now() - t) / (1000 * 60 * 60 * 24));
  if (days > 365) return 'overdue';
  if (days > 180) return 'due';
  return 'recommended';
}

// HEDIS labels with red-flag priority — coarse first pass.
const HIGH_PRIORITY_KEYWORDS = [
  'hba1c', 'a1c', 'retinal', 'nephropathy', 'eye',
  'colorectal', 'mammogram', 'cervical', 'depression',
];
function priorityForMeasure(label) {
  if (!label) return 'medium';
  const lower = String(label).toLowerCase();
  return HIGH_PRIORITY_KEYWORDS.some((kw) => lower.includes(kw)) ? 'high' : 'medium';
}

/**
 * Heuristic fallback — when no MeasureReports exist for the patient,
 * derive nudges from active Conditions via ICD-10 prefix lookup.
 * Returns the same row shape as the MeasureReport path so callers
 * can render either source identically.
 */
function deriveGapsFromConditions(conditionEntries) {
  const codes = conditionEntries
    .map((c) => c.resource?.code?.coding?.[0]?.code)
    .filter(Boolean);

  const measureMap = {
    E11: { measure: 'HbA1c (A1C control)', priority: 'high' },
    I10: { measure: 'BP Control', priority: 'medium' },
    F32: { measure: 'PHQ-9 Depression Screening', priority: 'high' },
    J44: { measure: 'Smoking Status / Cessation', priority: 'medium' },
  };

  const seen = new Set();
  const gaps = [];
  for (const code of codes) {
    const prefix = code?.substring(0, 3);
    const m = prefix ? measureMap[prefix] : null;
    if (!m || seen.has(prefix)) continue;
    seen.add(prefix);
    gaps.push({
      id: `heuristic-${prefix}`,
      gap: m.measure,
      status: 'recommended',
      lastDate: null,
      priority: m.priority,
      source: 'heuristic',
    });
  }
  return gaps;
}
